import { useFeatureFlagToggles } from '@clarity-website/app/ConfigurationContext';
import { installAzaSocketFreshness } from '@clarity-website/utils/azaSocketFreshness';
import useAzaEmployeeInfo from '@clarity-website/utils/useAzaEmployeeInfo';
import { lazy, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';

// Refreshes the AZA widget's idle websocket so its TA token never expires
// before a question is sent on it. See utils/azaSocketFreshness.ts.
const AZA_SOCKET_FRESHNESS_FLAG = 'aza-ws-ta-freshness';

// Dynamically import the AZA component
const AzaGlobalFab = lazy(() => import('atozassistant/AzaGlobalFab'));
const ClarityAssistDragAndDrop = lazy(
  () =>
    import(
      /* webpackChunkName: "clarity-assist-app" */ '@clarity-website/app/ClarityAssistDragAndDrop'
    ),
);

function AzaFabWrapper(): JSX.Element {
  const { isFeatureFlagToggleOn: useAzaGlobalFab } = useFeatureFlagToggles(
    'aza-mfe-integration',
  );
  // Off unless the flag exists, is enabled, and the user is on its allowList.
  const { isFeatureFlagToggleOnAndOnAllowList: refreshIdleAzaSockets } =
    useFeatureFlagToggles(AZA_SOCKET_FRESHNESS_FLAG);

  // Install in an effect (not during render), and hold the AZA widget back
  // until the effect has run so its first pre-connected socket is covered.
  const [socketGuardSettled, setSocketGuardSettled] = useState(false);
  useEffect(() => {
    if (useAzaGlobalFab && refreshIdleAzaSockets) {
      installAzaSocketFreshness();
    }
    setSocketGuardSettled(true);
  }, [useAzaGlobalFab, refreshIdleAzaSockets]);

  // AZA reads identity from window.AtoZContext on AtoZ-hosted pages; Clarity's
  // own origin never injects it, so pass employeeInfo explicitly (resolved from
  // the signed-in alias via PAPI). undefined until it resolves.
  const employeeInfo = useAzaEmployeeInfo();

  const ClarityAssist = <ClarityAssistDragAndDrop />;
  const history = useHistory();
  const handleLinkRedirection = (url: string) => {
    const targetUrl = new URL(url);
    if (targetUrl.host === window.location.host) {
      history.push(targetUrl.pathname + targetUrl.search + targetUrl.hash);
      return true;
    }
    return false;
  };

  return (
    <Suspense fallback={<span hidden />}>
      <ErrorBoundary fallback={ClarityAssist}>
        {useAzaGlobalFab
          ? socketGuardSettled &&
            (employeeInfo ? (
              <AzaGlobalFab
                customDevApiEndPoint={`https://${window.location.host}/apis/AtoZAssistant`}
                showFab
                employeeInfo={employeeInfo}
                handleLinkRedirection={handleLinkRedirection}
              />
            ) : (
              ClarityAssist
            ))
          : ClarityAssist}
      </ErrorBoundary>
    </Suspense>
  );
}

export default AzaFabWrapper;
